import { select } from 'd3-selection';
import type * as d3 from 'd3-selection';

import { formatBytes } from '../../../lib/formatBytes';
import { entityIconSvg, getEntityChipStyle } from '../../../lib/entityIcons';
import {
  resolveInsideCenterLabelLayout,
  resolveOutsideChipLayout,
  truncateChipTextToBudget,
  type InsideCenterLabelLine,
} from '../../../lib/bubble/bubbleHelpers';
import { setSvgTextFill } from '../../../lib/bubble/svgTextFill';
import type { PackLayoutNode } from '../../../lib/bubble/packNodeTypes';
import type { ContentGroupSelection, ChipParentSelection } from '../../../lib/d3/d3SelectionTypes';
import { type ChipElement, type ChipOptions, type CounterScaleText } from '../bubbleDomTypes';
import { isPinnedEmphasisChip, reapplyInfraChipDimIfNeeded } from '../bubbleEmphasis';
import { infraBubbleStyles as s, isInfraHierarchyType } from '../bubbleInfraStyles';
import type { BubbleRenderOptions } from './bubbleRenderOptions';
import { showBubbleTooltip } from './bubbleTooltip';
import { dispatchBubbleClickAction, isBubbleNodeClickable } from './bubbleClickDispatch';

export type { ChipOptions } from '../bubbleDomTypes';

export function shouldAddPlainInsideHitTarget(meta: {
  plain?: boolean;
  sizingMode?: string;
  _linkedNode?: unknown;
  _clickable?: boolean;
}): boolean {
  return Boolean(meta.plain && meta.sizingMode === 'inside' && meta._linkedNode && meta._clickable);
}

export function plainInsideHitRadius(anchorR: number, k: number, invFit: number): number {
  return (anchorR * k) / invFit;
}

export interface BubbleChipLayer {
  appendChip: (parent: ChipParentSelection, opts: ChipOptions) => unknown;
  appendBottomSizeChip: (
    parent: ChipParentSelection,
    d: PackLayoutNode,
    extras?: Partial<ChipOptions>
  ) => void;
  applyChipLayoutForK: (k: number) => void;
  applyTextCounterScale: (k: number) => void;
  getHierChipIconNode: (key: string) => Element | null;
  measureTextWidth: (text: string, fontSize: number, fontWeight?: number) => number;
  chipLayout: (typeof s)['chip']['layout'];
}

export function createBubbleChipLayer(deps: {
  invFit: number;
  contentG: ContentGroupSelection;
  options: BubbleRenderOptions;
}): BubbleChipLayer {
  const { invFit, contentG, options } = deps;

  const _hierChipIconNodeCache = new Map<string, Element>();
  let _hierChipIconCloneCounter = 0;
  function getHierChipIconNode(key: string): Element | null {
    const markup = entityIconSvg(key);
    if (!markup) return null;
    const cacheKey = `${key}\0${markup}`;
    if (!_hierChipIconNodeCache.has(cacheKey)) {
      const doc = new DOMParser().parseFromString(markup, 'image/svg+xml');
      _hierChipIconNodeCache.set(cacheKey, doc.documentElement);
    }
    const clone = _hierChipIconNodeCache.get(cacheKey)!.cloneNode(true) as Element;
    const idEls = clone.querySelectorAll('[id]');
    if (idEls.length) {
      const suffix = `-${key}-${_hierChipIconCloneCounter++}`;
      const idMap = new Map<string, string>();
      idEls.forEach((el) => {
        const oldId = el.getAttribute('id');
        if (!oldId) return;
        idMap.set(oldId, oldId + suffix);
        el.setAttribute('id', oldId + suffix);
      });
      clone.querySelectorAll('*').forEach((el) => {
        for (const attr of ['mask', 'fill', 'stroke', 'filter', 'clip-path']) {
          const v = el.getAttribute(attr);
          if (!v || !v.includes('url(#')) continue;
          let next = v;
          idMap.forEach((newId, oldId) => {
            next = next.replace(`url(#${oldId})`, `url(#${newId})`);
          });
          el.setAttribute(attr, next);
        }
      });
    }
    return clone;
  }

  let measureCtx: CanvasRenderingContext2D | null = null;
  const chipLayout = s.chip.layout;
  const iconLayout = s.icon;

  // Canvas 2D's `font` string is parsed independently of the CSS cascade and does not resolve
  // custom properties — passing a var() expression here silently no-ops the assignment (the
  // canvas spec leaves `font` at its previous value on a parse failure), so this concrete stack
  // is what backs the canvas-based measurer, while real DOM text elements below use the
  // var()-based chipFontFamily, which resolves correctly for them.
  const chipFontFamilyFallback = 'Inter, system-ui, sans-serif';

  function measureTextWidth(text: string, fontSize: number, fontWeight?: number) {
    if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
    if (!measureCtx) return 0;
    measureCtx.font = `${fontWeight || chipLayout.fontWeight} ${fontSize}px ${chipFontFamilyFallback}`;
    return measureCtx.measureText(text).width;
  }

  // Shared by the measurement node below and the real chip <text> elements so they can never
  // drift apart — a canvas 2D context is a different rendering pipeline from SVG text and can
  // only ever approximate it, so name/size chip width is measured off a real, hidden SVG <text>
  // instead, using the exact same font declaration as what's actually displayed.
  const chipFontFamily = `var(--font-family-sans, ${chipFontFamilyFallback})`;

  let measureTextNode: CounterScaleText | null = null;
  function measureRealTextWidth(text: string, fontSize: number, fontWeight?: number) {
    if (!measureTextNode) {
      measureTextNode = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'text'
      ) as CounterScaleText;
      measureTextNode.setAttribute('x', '-99999');
      measureTextNode.setAttribute('y', '-99999');
      measureTextNode.style.visibility = 'hidden';
      // Not a real chip label — keep it out of applyTextCounterScale's contentG-wide sweep,
      // same as every real chip <text> node does.
      measureTextNode.__skipCounterScale = true;
      contentG.node()!.appendChild(measureTextNode);
    }
    measureTextNode.setAttribute('font-size', String(fontSize));
    measureTextNode.setAttribute('font-weight', String(fontWeight || chipLayout.fontWeight));
    measureTextNode.setAttribute('font-family', chipFontFamily);
    measureTextNode.textContent = text;
    return measureTextNode.getBBox().width;
  }

  function renderChipContent(node: ChipElement, k: number) {
    const m = node.__chipMeta;
    if (!m) return;
    const { padX, padY, fontSize: defaultFontSize, minTextChars: minTextCharsForChip } = chipLayout;
    const dotSize = m.dotColor ? 7 : 0;
    const dotGap = m.dotColor ? 5 : 0;
    const iconW = m.icon ? 11 : 0;
    const iconGap = m.icon ? 3 : 0;
    const svgIconSize = m.svgIcon ? iconLayout.badgeSize : 0;
    const svgIconGap = m.svgIcon ? chipLayout.iconGap : 0;
    const chipFontSize = m.fontSize ?? defaultFontSize;
    const chipH = padY * 2 + chipFontSize + 2;
    const charW = chipFontSize * 0.56;

    const fullTextW = m.text ? measureRealTextWidth(m.text, chipFontSize, m.fontWeight) : 0;
    let fullChipW =
      padX * 2 + dotSize + dotGap + iconW + iconGap + svgIconSize + svgIconGap + fullTextW;
    const naturalFullChipW = fullChipW;
    const minChipW =
      padX * 2 +
      dotSize +
      (m.dotColor ? dotGap : 0) +
      iconW +
      (m.icon ? iconGap : 0) +
      svgIconSize +
      (m.svgIcon ? svgIconGap : 0);
    const iconOnlyW = padX * 2 + dotSize + iconW + svgIconSize;

    if (m.maxWidth && fullChipW > m.maxWidth) {
      fullChipW = Math.max(minChipW + charW * 3, m.maxWidth);
    }

    let show = true;
    let chipW = fullChipW;
    let displayText = m.text || '';
    let visibleCenterLines: InsideCenterLabelLine[] | null = null;
    if (m.sizingMode === 'inside') {
      const bubbleScreenDiam = (2 * m.anchorR * k) / invFit;
      if (m.lines?.length) {
        const layout = resolveInsideCenterLabelLayout({
          lines: m.lines,
          bubbleScreenDiam,
          measureWidth: measureTextWidth,
          minTextChars: minTextCharsForChip,
        });
        if (!layout.show) {
          show = false;
        } else {
          visibleCenterLines = layout.visibleLines;
        }
      }
      if (bubbleScreenDiam < minChipW) {
        show = false;
      } else if (bubbleScreenDiam < fullChipW + 4) {
        chipW = Math.max(minChipW, bubbleScreenDiam - 4);
      }
      displayText = truncateChipTextToBudget(
        displayText,
        chipW - minChipW,
        charW,
        minTextCharsForChip
      );
      if (
        show &&
        m._chipKind === 'center' &&
        m._linkedNode?.children?.some((c) => c.data?.type === 'volume')
      ) {
        show = false;
      } else if (show && m._linkedNode && m._linkedNode.children) {
        const chipWvb = (chipW * invFit) / k;
        const chipHvb = (chipH * invFit) / k;
        const cx = m.cx,
          cy = m.cy;
        for (const child of m._linkedNode.children) {
          const layoutChild = child as PackLayoutNode;
          if (!layoutChild.data || layoutChild.data.type !== 'volume') continue;
          if (
            Math.abs((layoutChild.x ?? 0) - cx) + (layoutChild.r ?? 0) <= chipWvb / 2 &&
            Math.abs((layoutChild.y ?? 0) - cy) + (layoutChild.r ?? 0) <= chipHvb / 2
          ) {
            show = false;
            break;
          }
        }
      }
    } else if (m.sizingMode === 'outside') {
      const bubbleScreenDiam = (2 * m.anchorR * k) / invFit;
      const pinnedChip = isPinnedEmphasisChip(node);
      if (m.minR && bubbleScreenDiam < m.minR && !pinnedChip) show = false;
      if (show) {
        const layout = resolveOutsideChipLayout({
          bubbleScreenDiam,
          naturalFullChipW,
          minChipW,
          iconOnlyW,
          fullChipW,
          maxWidth: m.maxWidth ?? null,
          text: m.text || '',
          charW,
          minTextChars: minTextCharsForChip,
          hasSvgIcon: !!m.svgIcon,
          measureWidth: (candidate) => measureRealTextWidth(candidate, chipFontSize, m.fontWeight),
        });
        if (!layout.show) {
          show = false;
        } else {
          chipW = layout.chipW;
          displayText = layout.displayText;
        }
      }
    }

    if (!show) {
      node.style.display = 'none';
      return;
    }
    node.style.display = '';
    node.setAttribute('transform', `translate(${m.cx},${m.cy}) scale(${invFit / k})`);

    while (node.firstChild) node.removeChild(node.firstChild);
    const svgNs = 'http://www.w3.org/2000/svg';
    if (shouldAddPlainInsideHitTarget(m)) {
      const hit = document.createElementNS(svgNs, 'circle');
      hit.setAttribute('cx', '0');
      hit.setAttribute('cy', '0');
      hit.setAttribute('r', String(plainInsideHitRadius(m.anchorR, k, invFit)));
      hit.setAttribute('fill', 'transparent');
      node.appendChild(hit);
    }
    if (!m.plain) {
      const rect = document.createElementNS(svgNs, 'rect');
      rect.setAttribute('x', String(-chipW / 2));
      rect.setAttribute('y', String(-chipH / 2));
      rect.setAttribute('width', String(chipW));
      rect.setAttribute('height', String(chipH));
      if (m._chipKind === 'name' || m._chipKind === 'size') {
        rect.setAttribute('rx', String(chipLayout.radius));
        rect.setAttribute('ry', String(chipLayout.radius));
      }
      rect.setAttribute('fill', m.bg ?? 'var(--color-white)');
      rect.setAttribute('stroke', m.border ?? s.chip.border);
      rect.setAttribute('stroke-width', String(m.strokeWidth || 1));
      rect.setAttribute('vector-effect', 'non-scaling-stroke');
      if (m._chipKind === 'size') {
        rect.setAttribute('filter', s.chip.sizeShadowFilter);
      }
      node.appendChild(rect);
    }

    let cursorX = -chipW / 2 + padX;
    if (m.dotColor) {
      const circle = document.createElementNS(svgNs, 'circle');
      circle.setAttribute('cx', String(cursorX + dotSize / 2));
      circle.setAttribute('cy', '0');
      circle.setAttribute('r', String(dotSize / 2));
      circle.setAttribute('fill', m.dotColor);
      node.appendChild(circle);
      cursorX += dotSize + dotGap;
    }
    if (m.svgIcon) {
      const iconNode = getHierChipIconNode(m.svgIcon);
      if (iconNode) {
        const badge = document.createElementNS(svgNs, 'g');
        badge.setAttribute('class', 'b-chip-icon-badge');
        const badgeBg = document.createElementNS(svgNs, 'rect');
        badgeBg.setAttribute('x', String(cursorX));
        badgeBg.setAttribute('y', String(-iconLayout.badgeSize / 2));
        badgeBg.setAttribute('width', String(iconLayout.badgeSize));
        badgeBg.setAttribute('height', String(iconLayout.badgeSize));
        badgeBg.setAttribute('rx', String(iconLayout.badgeRadius));
        badgeBg.setAttribute('ry', String(iconLayout.badgeRadius));
        badgeBg.setAttribute('fill', getEntityChipStyle(m.svgIcon).bg);
        badge.appendChild(badgeBg);
        const glyphPad = (iconLayout.badgeSize - iconLayout.glyphSize) / 2;
        iconNode.setAttribute('x', String(cursorX + glyphPad));
        iconNode.setAttribute('y', String(-iconLayout.glyphSize / 2));
        iconNode.setAttribute('width', String(iconLayout.glyphSize));
        iconNode.setAttribute('height', String(iconLayout.glyphSize));
        badge.appendChild(iconNode);
        node.appendChild(badge);
        cursorX += svgIconSize + (displayText ? svgIconGap : 0);
      }
    }
    if (m.icon === 'stack') {
      const ig = document.createElementNS(svgNs, 'g');
      ig.setAttribute('transform', `translate(${cursorX}, -6)`);
      const el = (cx_: number, cy_: number, rx_: number, ry_: number) => {
        const e = document.createElementNS(svgNs, 'ellipse');
        e.setAttribute('cx', String(cx_));
        e.setAttribute('cy', String(cy_));
        e.setAttribute('rx', String(rx_));
        e.setAttribute('ry', String(ry_));
        e.setAttribute('fill', m.fg ?? s.chip.text.name);
        e.setAttribute('opacity', '0.9');
        ig.appendChild(e);
      };
      const rc = (x_: number, y_: number, w_: number, h_: number) => {
        const r = document.createElementNS(svgNs, 'rect');
        r.setAttribute('x', String(x_));
        r.setAttribute('y', String(y_));
        r.setAttribute('width', String(w_));
        r.setAttribute('height', String(h_));
        r.setAttribute('fill', m.fg ?? s.chip.text.name);
        r.setAttribute('opacity', '0.9');
        ig.appendChild(r);
      };
      el(6, 2, 5, 1.6);
      rc(1, 2, 10, 4);
      el(6, 6, 5, 1.6);
      rc(1, 6, 10, 4);
      el(6, 10, 5, 1.6);
      node.appendChild(ig);
      cursorX += iconW + iconGap;
    }
    if (visibleCenterLines?.length) {
      const gap = 2;
      const lineH = visibleCenterLines.map((l) => l.fontSize * 1.15);
      const totalH = lineH.reduce((a, b) => a + b, 0) + gap * (visibleCenterLines.length - 1);
      let y = -totalH / 2;
      visibleCenterLines.forEach((line, i) => {
        y += lineH[i]!;
        const t = document.createElementNS(svgNs, 'text') as CounterScaleText;
        t.setAttribute('x', '0');
        t.setAttribute('y', String(y - lineH[i]! * 0.25));
        t.setAttribute('text-anchor', 'middle');
        t.setAttribute('font-size', String(line.fontSize));
        t.setAttribute('font-weight', String(line.fontWeight));
        t.setAttribute('font-family', chipFontFamily);
        setSvgTextFill(t, line.fg);
        t.textContent = line.text;
        t.__skipCounterScale = true;
        node.appendChild(t);
        y += gap;
      });
    } else if (displayText) {
      const t = document.createElementNS(svgNs, 'text') as CounterScaleText;
      t.setAttribute('x', String(cursorX));
      t.setAttribute('y', String(chipFontSize * 0.34));
      t.setAttribute('font-size', String(chipFontSize));
      t.setAttribute('font-weight', String(m.fontWeight ?? chipLayout.fontWeight));
      t.setAttribute('font-family', chipFontFamily);
      setSvgTextFill(t, m.fg ?? s.chip.text.name);
      t.textContent = displayText;
      t.__skipCounterScale = true;
      node.appendChild(t);
    }

    reapplyInfraChipDimIfNeeded(node);
  }

  function applyTextCounterScale(k: number) {
    (contentG.selectAll('text') as d3.Selection<CounterScaleText, unknown, Element, unknown>).each(
      function (this: CounterScaleText) {
        if (this.__skipCounterScale) return;
        if (this.__baseFs === undefined) {
          this.__baseFs = parseFloat(this.getAttribute('font-size') ?? '') || 11;
        }
        this.setAttribute('font-size', String(this.__baseFs / k));
      }
    );
  }

  function applyChipLayoutForK(k: number) {
    const candidates: Array<{
      node: ChipElement;
      cx: number;
      cy: number;
      hwVb: number;
      hhVb: number;
      priority: number;
      emphasis: boolean;
    }> = [];
    (contentG.selectAll('.b-chip') as d3.Selection<ChipElement, unknown, Element, unknown>).each(
      function (this: ChipElement) {
        renderChipContent(this, k);
        if (this.style.display === 'none') return;
        const m = this.__chipMeta;
        if (!m) return;
        if (m.plain) return;
        const rect = this.querySelector('rect');
        if (!rect) return;
        const w = parseFloat(rect.getAttribute('width') ?? '0');
        const h = parseFloat(rect.getAttribute('height') ?? '0');
        candidates.push({
          node: this,
          cx: m.cx,
          cy: m.cy,
          hwVb: (w * invFit) / 2,
          hhVb: (h * invFit) / 2,
          priority: isPinnedEmphasisChip(this) ? Infinity : m.anchorR || 0,
          emphasis: isPinnedEmphasisChip(this),
        });
      }
    );
    const emphasis = candidates.filter((c) => c.emphasis);
    const regular = candidates.filter((c) => !c.emphasis);
    emphasis.sort((a, b) => b.priority - a.priority);
    regular.sort((a, b) => b.priority - a.priority);
    const shown = [...emphasis];
    const PAD = 2;
    for (const c of regular) {
      let blocked = false;
      for (const s of shown) {
        const dx = Math.abs(c.cx - s.cx);
        const dy = Math.abs(c.cy - s.cy);
        if (dx * k < c.hwVb + s.hwVb + PAD && dy * k < c.hhVb + s.hhVb + PAD) {
          blocked = true;
          break;
        }
      }
      if (blocked) c.node.style.display = 'none';
      else shown.push(c);
    }
  }

  function appendChip(parent: ChipParentSelection, opts: ChipOptions) {
    const g = parent.append('g').attr('class', 'b-chip');
    const node = g.node() as ChipElement | null;
    if (!node) return g;
    if (opts.chipKind) {
      node.setAttribute('data-chip-kind', opts.chipKind);
    }
    node.__chipMeta = {
      cx: opts.cx,
      cy: opts.cy,
      anchorR: opts.anchorR || 0,
      text: opts.text,
      dotColor: opts.dotColor || null,
      icon: opts.icon || null,
      bg: opts.bg || 'var(--color-white)',
      fg: opts.fg || s.chip.text.name,
      border: opts.border || s.chip.border,
      fontSize: opts.fontSize || chipLayout.fontSize,
      fontWeight: opts.fontWeight || chipLayout.fontWeight,
      maxWidth: opts.maxWidth || null,
      hoverText: opts.hoverText || null,
      sizingMode: opts.sizingMode || 'outside',
      minR: opts.minR || 0,
      _linkedNode: opts.linkedNode || null,
      _chipKind: opts.chipKind || null,
      svgIcon: opts.svgIcon || null,
      plain: opts.plain || false,
      lines: opts.lines || null,
    };
    const meta = node.__chipMeta;
    if (!meta) return g;
    if (meta._linkedNode) {
      meta._clickable = isBubbleNodeClickable(options.location, options.view, meta._linkedNode, {
        secondary: options.secondary,
        tagKey: options.tagKey,
      });
    }
    renderChipContent(node, 1);
    if (meta._linkedNode) {
      const linkedNode = meta._linkedNode;
      const clickable = meta._clickable;
      node.style.pointerEvents = 'all';
      node.style.cursor = clickable ? 'pointer' : 'default';
      const forwardOrHandleHover = (
        type: 'mouseenter' | 'mouseleave',
        e: MouseEvent,
        hovered: boolean
      ) => {
        const circle = options.getBubbleCircleForNode?.(linkedNode);
        if (circle) {
          circle.dispatchEvent(new MouseEvent(type, { clientX: e.clientX, clientY: e.clientY }));
          return;
        }
        if (isInfraHierarchyType(linkedNode?.data?.type)) {
          options.applyInfraHover?.(linkedNode, hovered);
        }
        if (hovered) {
          showBubbleTooltip(e, linkedNode, options);
        } else {
          options.hideTooltip();
        }
      };
      node.addEventListener('mouseenter', (e) => forwardOrHandleHover('mouseenter', e, true));
      node.addEventListener('mouseleave', (e) => forwardOrHandleHover('mouseleave', e, false));
      if (clickable) {
        node.addEventListener('click', (e) => {
          dispatchBubbleClickAction(e, linkedNode, options);
        });
      }
    }
    return g;
  }

  function sizeChipText(d: PackLayoutNode) {
    const volCount = d.data.count != null ? d.data.count : d.leaves ? d.leaves().length : 0;
    return `${formatBytes(d.data.value)} (${(volCount || 0).toLocaleString()})`;
  }

  function appendBottomSizeChip(
    parent: ChipParentSelection,
    d: PackLayoutNode,
    extras: Partial<ChipOptions> = {}
  ) {
    appendChip(parent, {
      cx: d.x,
      cy: d.y + d.r,
      anchorR: d.r,
      linkedNode: d,
      chipKind: 'size',
      text: sizeChipText(d),
      bg: s.chip.bg,
      fg: s.chip.text.size,
      border: s.chip.border,
      fontSize: chipLayout.fontSize,
      fontWeight: chipLayout.fontWeight,
      sizingMode: 'outside',
      minR: chipLayout.size.minR,
      maxWidth: chipLayout.size.maxWidth,
      ...extras,
    });
  }

  return {
    appendChip,
    appendBottomSizeChip,
    applyChipLayoutForK,
    applyTextCounterScale,
    getHierChipIconNode,
    measureTextWidth,
    chipLayout,
  };
}
